import { useLazyQuery, useQuery } from "@apollo/client";
import { FC, useEffect } from "react";
import { useParams } from "react-router-dom";
import CompanyInfo from "./CompanyInfo";
import { ONBOARDING } from "../../../graphql/onboarding/onboarding/query";
import { COMPANY_INFO_PAPPERS } from "../../../graphql/onboarding/papers/query";
import { DOCUMENTS_TYPE } from "../../../graphql/onboarding/document/query";
import { OnboardingInfo } from "../../../graphql/onboarding/onboarding/types/OnboardingInfo";
import {
  FetchCompanyPappers,
  FetchCompanyPappersVariables,
} from "../../../graphql/onboarding/papers/types/FetchCompanyPappers";
import {
  DocumentsType,
  DocumentsTypeVariables,
} from "../../../graphql/onboarding/document/types/DocumentsType";
import { CorporateInfo } from "../../../graphql/onboarding/corporate/types/CorporateInfo";
import { DocumentType } from "../../../enums/document";
import { DEFAULT_COUNTRY_CODE } from "../../../constants/countries";
import {
  CreateCompanyInput,
  CreateCorporateInput,
} from "../../../types/graphql-global-types-onboarding";
import Layout from "../../Common/NewLayout";
import Header from "../../Common/NewLayout/Header";
import { useToastNotifications } from "../../../utils/toastUtils";
import { CompanyInfo as Company } from "../../../graphql/onboarding/company/types/CompanyInfo";

const documentTypes = [
  DocumentType.COMPANY_REGISTRY_EXTRACT,
  DocumentType.ARTICLE_OF_ASSOCIATION,
  DocumentType.PROOF_OF_ADDRESS,
];

let defaultCorporate: CreateCompanyInput = {
  name: "",
  code: "",
  legalFormId: null,
  legalFormLabel: null,
  leiCode: null,
  taxpayerIdentificationNumber: null,
  registrationDate: undefined,
  countryTaxResidenceIsoCode: "FR",
  address: "",
  city: "",
  postcode: "",
  countryIsoCode: "FR",
  legalAddress: "",
  legalCity: "",
  legalPostcode: "",
  legalCountryIsoCode: "FR",
  isIndividual: null,
  documents: [],
  accountPurposes: "",
  expectedMonthlyExpensesValue: "",
};

interface RouteParams extends Record<string, string | undefined> {
  incorporationCode: string;
}
const CompanyInfoProvider: FC = () => {
  const { incorporationCode } = useParams<RouteParams>();
  const { showErrorToast } = useToastNotifications();
  const [getInfo, { data: companyPappers, loading: pappersLoading }] =
    useLazyQuery<FetchCompanyPappers, FetchCompanyPappersVariables>(
      COMPANY_INFO_PAPPERS,
      { fetchPolicy: "no-cache" },
    );

  const { data: onboarding, loading } = useQuery<OnboardingInfo>(ONBOARDING, {
    fetchPolicy: "network-only",
    onError: () => {
      showErrorToast({
        description: `onboarding.retrieving.information.error`,
      });
    },
  });

  const { data: typesDocument, loading: loadingDocumentsType } = useQuery<
    DocumentsType,
    DocumentsTypeVariables
  >(DOCUMENTS_TYPE, {
    fetchPolicy: "network-only",
    variables: { types: documentTypes },
  });

  useEffect(() => {
    if (incorporationCode && incorporationCode !== "create") {
      getInfo({ variables: { siret: incorporationCode, documentTypes } });
    }
  }, [incorporationCode]);

  const assignCorporateData = (company: Company) => {
    Object.assign(defaultCorporate, company);

    if (company.documents?.length) {
      defaultCorporate.documents = company.documents
        .filter(
          (d) =>
            d?.type?.type !== undefined &&
            d?.type?.type !== null &&
            documentTypes.includes(d.type.type as DocumentType),
        )
        .map((d) => ({
          filename: d?.filename,
          fileType: d?.fileType,
          base64Content: d?.base64Content,
          type: d?.type?.type as DocumentType,
        }));
    }
  };

  if (onboarding?.onboardingInfo?.company?.name) {
    assignCorporateData(onboarding.onboardingInfo.company);
  }

  if (
    companyPappers?.fetchCompanyPappers?.company &&
    onboarding?.onboardingInfo?.company?.code !== incorporationCode
  ) {
    assignCorporateData(companyPappers.fetchCompanyPappers.company);
  }

  return (
    <Layout
      title="Informations"
      description="Indiquez les informations de votre société et chargez les documents nécessaire."
      isDoubleContent
      loading={loading || loadingDocumentsType || pappersLoading}
    >
      <Header
        title="Informations sur l'entreprise"
        description="Indiquez les informations de votre société."
      />
      <CompanyInfo
        corporateSaved={defaultCorporate}
        previous={
          incorporationCode ? `/company/${incorporationCode}` : `/company`
        }
        disable={false}
        documentsType={typesDocument?.documentsType ?? []}
        isAutocomplete={Boolean(
          incorporationCode && incorporationCode !== "create",
        )}
      />
    </Layout>
  );
};

export default CompanyInfoProvider;
